import { type RefObject, useMemo, useRef } from 'react';
import { useABSmartlyContext } from '../context/ABSmartlyProvider';
import { getVariantsArray } from '../utils/variants';

type ReturnType<TVariants extends readonly string[]> = {
  decisions: RefObject<Record<string, number | null>>;
  isReady: boolean;
  singleDecision: RefObject<null | number>;
  results: (TVariants[number] | null)[];
};

export const useClientABSmartlyExperiments = <TVariants extends readonly string[]>(
  experimentIds: string | string[],
): ReturnType<TVariants> => {
  const context = useABSmartlyContext();
  const treatments = Array.isArray(experimentIds)
    ? experimentIds.reduce(
        (acc, experimentId) => {
          return {
            ...acc,
            [experimentId]: null,
          };
        },
        {} as Record<string, number | null>,
      )
    : {};
  const singleDecision = useRef<null | number>(null);
  const decisions = useRef<Record<string, number | null>>(treatments);

  return useMemo(() => {
    const evaluateExperiment = (experimentId: string) => {
      if (!experimentId || !context || !context.isReady()) {
        return null;
      }
      const variant = context.treatment(experimentId);
      if (Array.isArray(experimentIds)) {
        decisions.current = {
          ...decisions.current,
          [experimentId]: variant,
        };
      } else {
        singleDecision.current = variant;
      }
      const variants = getVariantsArray(context, experimentId);
      const variantName = variants[variant];
      return variantName;
    };
    const results = Array.isArray(experimentIds)
      ? experimentIds.map(experimentId => evaluateExperiment(experimentId))
      : [evaluateExperiment(experimentIds)];

    return {
      isReady: !!context?.isReady(),
      results,
      singleDecision,
      decisions,
    };
  }, [context, experimentIds]);
};
